/**
 * <MyDashboardSummary /> — landing tab of My Dashboard.
 *
 * Aggregates task load, goals, meetings today/tomorrow, timesheet completion,
 * activity streak into a single "at a glance" view.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Clock, AlertCircle, Target, Video, Calendar, TrendingUp, Flame, ShieldCheck } from "lucide-react";

function Stat({ icon: Icon, label, value, tone = "", testid, hint }) {
  return (
    <div className="border border-border p-4 space-y-1" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`text-2xl font-bold font-mono leading-tight ${tone}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

const PRIORITY_TONES = {
  urgent: "border-red-500/40 text-red-700 bg-red-500/5",
  high: "border-amber-500/40 text-amber-700 bg-amber-500/5",
  medium: "border-sky-500/40 text-sky-700 bg-sky-500/5",
  low: "border-border text-muted-foreground",
};

export default function MyDashboardSummary({ onNavigate }) {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let alive = true;
    setLoading(true);
    api.get(`/me/dashboard/summary?ws_id=${activeWorkspaceId}`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeWorkspaceId]);

  if (loading) return <div className="text-sm text-muted-foreground py-6">Loading your dashboard…</div>;
  if (!data) return null;
  const t = data.tasks || {};
  const g = data.goals || {};
  const m = data.meetings || {};
  const ts = data.timesheet || {};
  const a = data.activity || {};
  const c = data.compliance || {};
  const priorityEntries = Object.entries(t.by_priority || {}).sort();
  const streak = a.streak_pct || 0;

  return (
    <div className="space-y-6" data-testid="my-dashboard-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={CheckCircle2} label="Open tasks" value={t.open || 0} testid="stat-open-tasks" />
        <Stat icon={AlertCircle} label="Overdue" value={t.overdue || 0} tone={t.overdue > 0 ? "text-red-600" : ""} testid="stat-overdue" />
        <Stat icon={Clock} label="Due today" value={t.due_today || 0} tone={t.due_today > 0 ? "text-amber-600" : ""} testid="stat-due-today" />
        <Stat icon={Clock} label="Due this week" value={t.due_this_week || 0} testid="stat-due-week" />
        <Stat icon={Target} label="Active goals" value={g.active || 0} testid="stat-goals" hint={g.active > 0 ? `Avg ${g.avg_progress}% progress` : undefined} />
        <Stat icon={Video} label="Meetings today+tomorrow" value={m.count || 0} testid="stat-meetings" />
        <Stat icon={Calendar} label="Hours this week" value={ts.hours_this_week || 0} testid="stat-timesheet" hint={`${ts.submitted_days || 0} day${ts.submitted_days === 1 ? "" : "s"} submitted`} />
        <Stat icon={Flame} label="Activity streak" value={`${streak}%`} tone={streak >= 70 ? "text-emerald-600" : streak >= 40 ? "text-amber-600" : ""} testid="stat-streak" hint={`${a.days_active_last_14 || 0}/14 days active`} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-border p-6 space-y-3" data-testid="my-dashboard-priorities">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Task priority mix</div>
            <Link to="?tab=tasks" onClick={() => onNavigate?.("tasks")} className="text-xs text-primary hover:underline">Open tasks →</Link>
          </div>
          <div className="space-y-1.5">
            {priorityEntries.length === 0 ? (
              <div className="text-xs text-muted-foreground">No open tasks. Nice.</div>
            ) : priorityEntries.map(([prio, count]) => (
              <div key={prio} className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${PRIORITY_TONES[prio] || ""}`}>{prio}</span>
                <div className="flex-1 h-1.5 bg-secondary">
                  <div
                    className={`h-full ${prio === "urgent" ? "bg-red-500" : prio === "high" ? "bg-amber-500" : prio === "medium" ? "bg-sky-500" : "bg-muted-foreground"}`}
                    style={{ width: `${Math.round((count * 100) / Math.max(1, t.open || 1))}%` }}
                  />
                </div>
                <span className="text-xs font-mono w-6 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border p-6 space-y-3" data-testid="my-dashboard-goals">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow flex items-center gap-2"><Target className="w-3.5 h-3.5" /> My goals</div>
            <Link to="?tab=goals" onClick={() => onNavigate?.("goals")} className="text-xs text-primary hover:underline">Open goals →</Link>
          </div>
          {(g.top || []).length === 0 ? (
            <div className="text-xs text-muted-foreground">No active goals. Set one to track progress.</div>
          ) : (
            <div className="space-y-2">
              {(g.top || []).map((goal) => (
                <div key={goal.id} className="flex items-center gap-3">
                  <span className="text-xs flex-1 truncate">{goal.title}</span>
                  <div className="flex-1 h-1.5 bg-secondary">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, goal.progress || 0)}%` }} />
                  </div>
                  <span className="text-xs font-mono w-10 text-right">{Math.round(goal.progress || 0)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="border border-border p-6 space-y-3" data-testid="my-dashboard-top-projects">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" /> Your busy projects</div>
          <span className="text-xs text-muted-foreground">Top 5 by your open tasks</span>
        </div>
        {(t.top_projects || []).length === 0 ? (
          <div className="text-xs text-muted-foreground">No project-scoped tasks assigned right now.</div>
        ) : (
          <div className="space-y-1.5">
            {(t.top_projects || []).map((p) => (
              <Link
                key={p.id}
                to={`/app/projects/${p.id}`}
                className="flex items-center justify-between border border-border/60 hover:border-primary/50 p-3 transition group"
                data-testid={`my-dashboard-project-${p.id}`}
              >
                <span className="text-sm font-medium truncate group-hover:text-primary transition">{p.name}</span>
                <span className="text-xs font-mono"><span className="font-bold">{p.open_tasks}</span> open</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {(m.today_tomorrow || []).length > 0 && (
        <div className="border border-border p-6 space-y-3" data-testid="my-dashboard-meetings">
          <div className="label-eyebrow flex items-center gap-2"><Video className="w-3.5 h-3.5" /> Meetings — today &amp; tomorrow</div>
          <div className="space-y-1.5">
            {m.today_tomorrow.map((meeting) => (
              <div key={meeting.id} className="flex items-center justify-between border border-border/60 p-2.5 text-xs" data-testid={`my-dashboard-meeting-${meeting.id}`}>
                <span className="truncate">{meeting.title || "Untitled meeting"}</span>
                <span className="font-mono text-muted-foreground">{(meeting.start_time || "").replace("T", " ").slice(0, 16)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* r-compliance-tile · "Your compliance today" — surfaces the daily
          sweep for the logged-in user so they can self-serve before HR
          emails go out. Tile stays subtle when everything's green,
          shows red only when there's an open violation. */}
      <div
        className={`border p-6 space-y-3 ${c.open_violations > 0 ? "border-red-300 bg-red-50/40" : "border-emerald-300 bg-emerald-50/30"}`}
        data-testid="my-dashboard-compliance-tile"
      >
        <div className="flex items-center justify-between">
          <div className="label-eyebrow flex items-center gap-2">
            <ShieldCheck className={`w-3.5 h-3.5 ${c.open_violations > 0 ? "text-red-600" : "text-emerald-600"}`} />
            Your compliance {c.date ? `· ${c.date}` : ""}
          </div>
          <Link
            to="/app/reports/compliance-monitor"
            className="text-xs text-primary hover:underline"
            data-testid="my-dashboard-compliance-link"
          >
            Open monitor →
          </Link>
        </div>
        <div className="flex items-baseline gap-6 flex-wrap">
          <div>
            <div className={`text-3xl font-bold font-mono ${c.open_violations > 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="my-dashboard-compliance-count">
              {c.open_violations || 0}
            </div>
            <div className="text-[11px] text-muted-foreground">open violation{c.open_violations === 1 ? "" : "s"}</div>
          </div>
          {c.streak_days > 0 && (
            <div>
              <div className="text-3xl font-bold font-mono text-orange-600 flex items-center gap-1">
                <Flame className="w-5 h-5" /> {c.streak_days}
              </div>
              <div className="text-[11px] text-muted-foreground">day compliance streak</div>
            </div>
          )}
        </div>
        {(c.items || []).length > 0 && (
          <ul className="space-y-1.5 border-t border-border/60 pt-3">
            {(c.items || []).map((v) => (
              <li key={v.id} className="text-xs flex items-start gap-2" data-testid={`my-dashboard-compliance-item-${v.id}`}>
                <span
                  className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${
                    v.severity === "high" ? "bg-red-500 text-white"
                      : v.severity === "medium" ? "bg-amber-400 text-amber-950"
                      : "bg-slate-300 text-slate-800"
                  }`}
                >
                  {v.severity || "medium"}
                </span>
                <div className="min-w-0">
                  <div className="font-medium">{v.label}</div>
                  {v.detail && <div className="text-[11px] text-muted-foreground">{v.detail}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
        {(c.open_violations || 0) === 0 && (
          <div className="text-xs text-emerald-700">All clear — thanks for staying on top of it. 🎉</div>
        )}
      </div>
    </div>
  );
}
